/**
 * Cars tab — THE one list, rendered identically in every mode
 * (mockup tpl-cars).
 *
 * Row = 3-part stack:
 *   header  number chip + crew name (12px)
 *   media   16:9 thumb (flex-1) + 86px telemetry column (Pos/Gap/Spd)
 *   sub     make/model (10px muted)
 *
 * Density toggle preview ↔ list in the toolbar (list = 96px thumb
 * left, telemetry column hidden), persisted in localStorage by the
 * orchestrator and shared across modes. On phones (<lg) the design's
 * Cars tab is the list-density row verbatim — density
 * is forced to 'list' and the toggle hidden.
 *
 * Rows group under Following / All-on-stage headers (mockup
 * .cars-group-h); a mobile-only search filters by number, crew,
 * vehicle or device id (mockup .m-search).
 *
 * Thumb lag tags use the shared stream-health scale (lag.ts) — the
 * orchestrator decides per device whether the value is true player lag
 * (active camera) or telemetry-age proxy.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { List, Play, Rows2, Search, Star, Timer } from 'lucide-react';

import { cn, displayVehicle } from '@/lib/utils';
import { EventLiveDevice, isLiveNow } from '../../hooks/useEventLiveDevices';
import { LG_MEDIA_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { compareCarNumbers, displayCarNumber } from './carNumber';
import LagChip from './LagChip';
import { LagInfo, useNowTick } from './lag';
import type { RecordingStatus } from './recordingStatus';
import { Standing } from './useEventStandings';

export type CarsDensity = 'preview' | 'list';

/** Stage filter passed down from the orchestrator when a StageStrip
 *  chip is active: label ('SS2') + per-device footage lookup. */
export interface StageSel {
  label: string;
  has: (deviceId: string) => boolean;
  /** The session whose poster the thumb should show for
   *  this stage (the attributed session picked by the orchestrator). */
  sessionFor?: (deviceId: string) => string | undefined;
}

const Thumb: React.FC<{
  isLive: boolean;
  replay: boolean;
  hasSession: boolean;
  /** Race number — ghosted into the tile; stays as the fallback when
   *  no poster frame exists yet. */
  carNumber: string;
  /** Session whose cached poster frame fills the tile. */
  posterSessionId?: string | null;
  /** 'SS2'/'H1' when a stage filter is on and this car HAS footage
   *  there — renders as the corner tag instead of the generic VOD. */
  stageTag: string | null;
  /** Armed but no position yet — flagged so the row reads as "we know
   *  about this car, it just isn't on the map". */
  awaitingFix: boolean;
  lag: LagInfo;
}> = ({ isLive, replay, hasSession, carNumber, posterSessionId, stageTag, awaitingFix, lag }) => {
  // Poster fetch is best-effort: a 404 (no footage yet / ffmpeg miss)
  // simply keeps the placeholder. Keyed reset per session.
  const [posterFailed, setPosterFailed] = useState(false);
  useEffect(() => setPosterFailed(false), [posterSessionId]);
  const showPoster = posterSessionId != null && !posterFailed && (isLive || hasSession);
  return (
  <div
    className={cn(
      'relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-sm border border-border-subtle',
      !isLive && !hasSession && 'opacity-55',
    )}
    style={{
      // Live tiles warm up slightly so the rail reads at a glance.
      background: isLive
        ? 'linear-gradient(135deg,#2b1c10 0%,#0a0a14 70%)'
        : 'linear-gradient(135deg,#1a1a2e 0%,#0a0a14 100%)',
    }}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-2 -right-1 select-none font-mono text-[42px] font-bold leading-none text-white/[0.07]"
    >
      {carNumber}
    </span>
    {isLive || hasSession ? (
      <Play className="size-5 text-white/50" />
    ) : (
      <Timer className="size-5 text-white/40" />
    )}
    {isLive && (
      <span className="absolute left-1 top-1 z-[1] rounded-[3px] bg-status-error px-1 py-px text-[8px] font-bold tracking-wide text-white">
        LIVE
      </span>
    )}
    {!isLive && stageTag != null && (
      <span className="absolute left-1 top-1 z-[1] rounded-[3px] bg-black/70 px-1 py-px text-[8px] font-bold tracking-wide text-accent-cyan">
        {stageTag}
      </span>
    )}
    {!isLive && stageTag == null && replay && hasSession && (
      <span className="absolute left-1 top-1 z-[1] rounded-[3px] bg-black/70 px-1 py-px text-[8px] font-bold tracking-wide text-accent-cyan">
        VOD
      </span>
    )}
    {awaitingFix && (
      <span className="absolute right-1 top-1 z-[1] rounded-[3px] bg-black/70 px-1 py-px text-[8px] font-bold tracking-wide text-status-lagging">
        NO GPS
      </span>
    )}
    {showPoster && (
      <img
        src={`/api/streams/sessions/${encodeURIComponent(posterSessionId!)}/poster.jpg`}
        alt=""
        loading="lazy"
        onError={() => setPosterFailed(true)}
        className="absolute inset-0 h-full w-full object-cover"
      />
    )}
    <LagChip lag={lag} className="absolute bottom-1 right-1 z-[1]" />
  </div>
  );
};

const SideCell: React.FC<{ label: string; value: React.ReactNode; dim?: boolean }> = ({
  label,
  value,
  dim = false,
}) => (
  <div className="flex min-h-0 flex-1 flex-col justify-center rounded-sm bg-bg-surface px-2 py-1">
    <span className="text-[8px] font-bold uppercase tracking-wide text-fg-muted">{label}</span>
    <span className={cn('font-mono text-[13px] font-bold leading-tight', dim ? 'text-fg-muted' : 'text-fg')}>
      {value}
    </span>
  </div>
);

const CarRow: React.FC<{
  device: EventLiveDevice;
  replay: boolean;
  density: CarsDensity;
  selected: boolean;
  lag: LagInfo;
  standing: Standing | null;
  /** Stage filter (StageStrip): label + footage lookup; null = All. */
  stageSel: StageSel | null;
  /** Truthful status for THIS row, only ever non-null for the car whose
   *  session is actually mounted in a player right now (recordingStatus.ts)
   *  — every other row has no way to know anything more than the SSE
   *  device feed below. */
  recordingStatus: RecordingStatus | null;
  nowMs: number;
  onSelect: (id: string) => void;
}> = ({ device, replay, density, selected, lag, standing, stageSel, recordingStatus, nowMs, onSelect }) => {
  const rawIsLive = !replay && isLiveNow(device, nowMs);
  const isLive = rawIsLive && (recordingStatus == null || recordingStatus === 'recording');
  // Stage filter active: the thumb reflects footage ON THAT STAGE, not
  // the generic live/last-session state — cars without it dim.
  const stageHas = stageSel != null ? stageSel.has(device.device_id) : null;
  const hasSession = stageHas ?? (device.session_id != null);
  const name = device.driver_name || device.device_id;
  const speed =
    device.speed_mps != null ? `${Math.round(device.speed_mps * 3.6)}` : '—';
  const carNumber = displayCarNumber(device);

  const header = (
    <div className="flex min-w-0 items-center gap-1.5">
      <span
        className={cn(
          'shrink-0 rounded-[3px] px-1.5 py-px font-mono text-[11px] font-bold',
          isLive || (replay && device.session_id)
            ? 'bg-accent text-fg-inverse'
            : 'bg-fg-muted text-white',
        )}
      >
        {carNumber}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs font-semibold leading-tight text-fg">
        {name}
      </span>
    </div>
  );
  const sub = (
    <div className="truncate text-[10px] leading-tight text-fg-muted">
      {displayVehicle(device.vehicle) || '—'}
    </div>
  );
  const thumb = (
    <Thumb
      isLive={isLive}
      replay={replay}
      hasSession={hasSession}
      carNumber={carNumber}
      posterSessionId={
        stageSel != null ? stageSel.sessionFor?.(device.device_id) : device.session_id
      }
      stageTag={stageHas ? stageSel!.label : null}
      awaitingFix={!replay && !device.has_fix}
      lag={lag}
    />
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(device.device_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault(); // Space would also scroll the panel
          onSelect(device.device_id);
        }
      }}
      className={cn(
        'cursor-pointer rounded-md px-1.5 py-2 transition-colors hover:bg-bg-surface',
        selected && 'bg-bg-elevated shadow-[inset_2px_0_0_var(--accent-primary)]',
      )}
    >
      {density === 'list' ? (
        // Compact: 96px thumb left, name + sub right, telemetry hidden.
        <div className="grid grid-cols-[96px_1fr] items-center gap-x-2.5">
          {thumb}
          <div className="flex min-w-0 flex-col gap-0.5">
            {header}
            {sub}
          </div>
        </div>
      ) : (
        // Preview: 3-part stack — header / media / sub.
        <div className="flex flex-col gap-1.5">
          {header}
          <div className="grid grid-cols-[1fr_86px] items-stretch gap-2">
            {thumb}
            <div className="flex min-w-0 flex-col gap-1">
              {/* Pos/Gap: circuit best-lap standings (useEventStandings);
                  "—" until a device completes its first lap (and always
                  on rally events — no standings source yet). */}
              <SideCell
                label="Pos"
                value={standing ? `P${standing.pos}` : '—'}
                dim={!standing}
              />
              <SideCell
                label="Gap"
                value={
                  standing
                    ? standing.gapMs === 0
                      ? 'LEAD'
                      : `+${(standing.gapMs / 1000).toFixed(1)}`
                    : '—'
                }
                dim={!standing}
              />
              <SideCell label="Spd" value={speed} dim={speed === '—'} />
            </div>
          </div>
          {sub}
        </div>
      )}
    </div>
  );
};

/** Section header (mockup .cars-group-h): Following / All on stage. */
const GroupHeader: React.FC<{
  left: React.ReactNode;
  right?: React.ReactNode;
  rightClass?: string;
}> = ({ left, right, rightClass }) => (
  <div className="flex items-center justify-between px-0.5 pb-1 pt-2.5 text-[9px] font-bold uppercase tracking-wider text-fg-muted">
    <span className="inline-flex items-center gap-1">{left}</span>
    {right != null && (
      <span className={cn('font-mono font-medium normal-case tracking-normal', rightClass)}>
        {right}
      </span>
    )}
  </div>
);

export const CarsTab: React.FC<{
  devices: EventLiveDevice[];
  liveCount: number;
  replay: boolean;
  density: CarsDensity;
  onDensityChange: (d: CarsDensity) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  lagFor: (device: EventLiveDevice) => LagInfo;
  /** Followed cars group under their own header at the top. */
  followedIds: Set<string>;
  /** Circuit best-lap standings; empty map for rally events. */
  standings: Map<string, Standing>;
  /** Active stage filter; null = All (default behavior). */
  stageSel?: StageSel | null;
  /** The device whose session is actually mounted in a player right now
   *  (null when no video is showing, e.g. plain Browse mode) — only that
   *  ONE row can be more truthful than the SSE device feed. */
  mountedDeviceId?: string | null;
  recordingStatus?: RecordingStatus | null;
}> = ({
  devices,
  liveCount,
  replay,
  density,
  onDensityChange,
  selectedId,
  onSelect,
  lagFor,
  followedIds,
  standings,
  stageSel = null,
  mountedDeviceId = null,
  recordingStatus = null,
}) => {
  const [query, setQuery] = useState('');
  // Phones render the design's list-density rows verbatim — no toggle below lg.
  const isDesktop = useMediaQuery(LG_MEDIA_QUERY);
  const effectiveDensity: CarsDensity = isDesktop ? density : 'list';
  const nowMs = useNowTick(1000);

  // Live first, then armed/idle; stable by enrollment number inside
  // each group so the list doesn't shuffle on every SSE frame.
  // (Followed cars sit in their own section above, same ordering.)
  const sorted = useMemo(() => {
    const score = (d: EventLiveDevice) => (isLiveNow(d, nowMs) ? 0 : 1);
    return [...devices].sort((a, b) => score(a) - score(b) || compareCarNumbers(a, b));
  }, [devices, nowMs]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (d) =>
        displayCarNumber(d).toLowerCase().includes(q) ||
        (d.driver_name ?? '').toLowerCase().includes(q) ||
        (d.vehicle ?? '').toLowerCase().includes(q) ||
        d.device_id.toLowerCase().includes(q),
    );
  }, [sorted, query]);

  const followed = useMemo(
    () => visible.filter((d) => followedIds.has(d.device_id)),
    [visible, followedIds],
  );
  const rest = useMemo(
    () => visible.filter((d) => !followedIds.has(d.device_id)),
    [visible, followedIds],
  );
  const restLive = replay ? 0 : rest.filter((d) => isLiveNow(d, nowMs)).length;

  const row = (d: EventLiveDevice) => (
    <CarRow
      key={d.device_id}
      device={d}
      replay={replay}
      density={effectiveDensity}
      selected={d.device_id === selectedId}
      lag={lagFor(d)}
      standing={standings.get(d.device_id) ?? null}
      stageSel={stageSel}
      recordingStatus={d.device_id === mountedDeviceId ? recordingStatus : null}
      nowMs={nowMs}
      onSelect={onSelect}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between px-3.5 pb-1 pt-2.5 font-mono text-[10px] text-fg-muted">
        <span>
          {devices.length} {devices.length === 1 ? 'car' : 'cars'}
          {!replay && ` · ${liveCount} live`}
          {replay && ' · replay'}
        </span>
        <div className="hidden overflow-hidden rounded-sm border border-border-subtle bg-bg-surface lg:flex">
          <button
            type="button"
            title="Preview — big thumbnails"
            onClick={() => onDensityChange('preview')}
            className={cn(
              'flex items-center justify-center px-2.5 py-1',
              density === 'preview'
                ? 'bg-bg-elevated text-accent'
                : 'text-fg-muted hover:text-fg',
            )}
          >
            <Rows2 className="size-3.5" />
          </button>
          <button
            type="button"
            title="List — compact rows"
            onClick={() => onDensityChange('list')}
            className={cn(
              'flex items-center justify-center px-2.5 py-1',
              density === 'list'
                ? 'bg-bg-elevated text-accent'
                : 'text-fg-muted hover:text-fg',
            )}
          >
            <List className="size-3.5" />
          </button>
        </div>
      </div>
      {/* Mobile search (mockup .m-search) */}
      <div className="shrink-0 px-3 pb-2 pt-1 lg:hidden">
        <label className="flex items-center gap-2 rounded-md border border-border-subtle bg-bg-input px-2.5 py-1.5">
          <Search className="size-3.5 shrink-0 text-fg-muted" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search number or name…"
            aria-label="Search cars by number, crew, vehicle or device id"
            className="w-full bg-transparent text-[11px] text-fg placeholder:text-fg-muted focus:outline-none"
          />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {visible.length === 0 ? (
          <div className="px-1 py-6 text-center font-mono text-[10px] text-fg-muted">
            {query.trim()
              ? 'no cars match'
              : replay
                ? 'no recorded runs'
                : 'no armed phones yet'}
          </div>
        ) : (
          <>
            {followed.length > 0 && (
              <>
                <GroupHeader
                  left={
                    <>
                      <Star className="size-2.5 text-accent" />
                      Following
                    </>
                  }
                  right={`${followed.length} ${followed.length === 1 ? 'crew' : 'crews'}`}
                  rightClass="text-accent"
                />
                {followed.map(row)}
              </>
            )}
            {rest.length > 0 && (
              <>
                <GroupHeader
                  left={replay ? 'All competitors' : 'All on stage'}
                  right={
                    !replay && restLive > 0
                      ? `● ${restLive}${followed.length > 0 ? ' more' : ''} live`
                      : null
                  }
                  rightClass="text-status-online"
                />
                {rest.map(row)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CarsTab;
